/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {useEffect, useState} from "react";
import {ConfirmationDialog} from "../../../../Common/ConfirmationDialog";
import {Col, Row} from "react-bootstrap";
import {service as ProjectService} from "../../../../../Services/ProjectService";
import {ProjectImportArea} from "./ProjectImportArea";
import {useFetchContextNode} from "../../../../../Hooks/ContextHooks";

export const ProjectImportModal = (props) => {
	const {
		contextNodeId,
		confirmationButtonMessage = "Import",
		cancelButtonMessage = "Cancel",
		deleteButtonMessage = null,
		disableConfirmationButton = null,
		disableDeleteButton = null,
		hiddenFields = [],
		onBeforeConfirm = null,
		onBeforeDelete = null,
		onHide = null,
        onReload = null,
		readOnly = false,
		show,
		title = 'Import Project',
		summaryComponent: SummaryComponent = null,
		warning = null,
	} = props;
	
	const [importing, setImporting] = useState(false);
	const [fileUploads, setFileUploads] = useState([]);
	const [queuedFiles, setQueuedFiles] = useState({});
	const [error, setError] = useState(null);
	const [done, setDone] = useState(false);
    
    // sort the files by name, so we process them in a consistent order
    const sortedFileUploads = [...fileUploads].sort(
        (a, b) => {
            return a?.fileName.localeCompare(b?.fileName);
        }
    );
	
	useEffect(() => {
		if (show) {
			setFileUploads([]);
			setQueuedFiles({});
			setError(null);
			setDone(false);
		}
	}, [show]);
	
	const performDryRun = async () => {
		for (const fileUpload of sortedFileUploads) {
			const fileUploadId = fileUpload.fileUploadId;
			if(queuedFiles[fileUploadId] == null) {
				const result = await dryRun(fileUploadId);
                setQueuedFileResult(fileUploadId, result);
			}
		}
    };
	
	const dryRun = async (fileUploadId) => {
		let result = null;
		try {
			const response = await ProjectService.ImportProject(fileUploadId, contextNodeId, true);
			if (response?.result) {
				result = response?.result;
			}
		} catch (error) {
			result = {
				error: error?.message,
				message: "",
				targetContextNodeId: null,
			};
		}
		return result;
	};
    
    const setQueuedFileResult = (fileUploadId, result) => {
        setQueuedFiles(prev => ({
            ...prev,
            [fileUploadId]: result,
        }));
    }

	const importProjects = async () => {
		setError(null);
		setImporting(true);
		try {
            for (const fileUpload of sortedFileUploads) {
                const fileUploadId = fileUpload.fileUploadId ?? null;
                const queuedFile = queuedFiles[fileUploadId] ?? null;
                const { message } = queuedFile;
				if (queuedFile?.fileUploadId) {
                    try {
                        setQueuedFileResult(fileUploadId, {...queuedFile, message: "Processing..." })
                        const response = await ProjectService.ImportProject(fileUploadId, contextNodeId, false);
                        if (response?.result) {
                            setQueuedFileResult(fileUploadId, response?.result);
                        }
                    } catch (error) {
                        setError("One of more files failed to import.");
                        setQueuedFileResult(fileUploadId, {
                            error: error?.message,
                            message: "",
                            targetContextNodeId: null,
                        });
                    }
				} else {
                    setQueuedFileResult(fileUploadId, {
                        message: message,
                        targetContextNodeId: null,
                    });
				}
			}
		} catch (error) {
			setError(error?.message);
		} finally {
			setImporting(false);
			setDone(true);
			onReload?.();
		}
    };
	
	// dry run
	useEffect(() => {
		void performDryRun();
	}, [fileUploads]);

	return <ConfirmationDialog
		className="import-modal"
		size="lg"
		Title={title}
		Body={[
			(warning ?? error) &&
			<Row key="error-row">
				<Col xs={12}>
					<p className="sb-model-viewer-validation-failure-text">{warning ?? error}</p>
				</Col>
			</Row>,

			<Row key="model-row">
				<Col xs={12}>
					<ProjectImportArea
						contextNodeId={contextNodeId}
						queuedFiles={queuedFiles}
						uploadedFiles={sortedFileUploads}
						setUploadedFiles={setFileUploads}
					/>
				</Col>
			</Row>
		]}
		Display={show}
		OnHide={onHide}
		OnClose={onHide}
		CancelButtonMessage={done ? 'Close' : cancelButtonMessage}
		OnConfirm={!done ? (() => void importProjects()) : null}
		ConfirmationButtonMessage={importing ? "Importing..." : confirmationButtonMessage}
		DisableConfirmationButton={!sortedFileUploads.length || importing}
	/>;
}
